'use client';
import React from 'react';
import cn from '@utils/cn';
import { Button } from '@ui/Button/Button';
import { TestimonialsType } from '@lib/sanity/queries/modules/testimonials/types';
import { ArrowLeftIcon, ArrowRightIcon } from '@sanity/icons';
import { GridColFullWidth } from '@constants/layout';
import { useState } from 'react';
import Anchor from '@ui/Anchor/Anchor';
import Text, { TextVariant } from '@ui/Text/Text';
import PortableText from '@ui/Text/PortableText';

type Props = {
  data: TestimonialsType;
  imageVisuals?: Record<string, React.ReactNode>;
  logoVisuals?: Record<string, React.ReactNode>;
};
export default function TestimonialsImageClient({ data, imageVisuals, logoVisuals }: Props) {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  if (!data || !data.testimonials?.length) {
    return null;
  }
  const testimonials = data.testimonials;

  const handleMoveForward = () => {
    setCurrentTestimonialIndex(currentTestimonialIndex === testimonials?.length - 1 ? 0 : currentTestimonialIndex + 1);
  };
  const handleMoveBackwards = () => {
    setCurrentTestimonialIndex(currentTestimonialIndex === 0 ? testimonials.length - 1 : currentTestimonialIndex - 1);
  };
  const imageId = testimonials[currentTestimonialIndex].image?._id;
  const logoId = testimonials[currentTestimonialIndex].logo?._id;

  return (
    <div className={cn('mx-auto w-full', GridColFullWidth)}>
      {testimonials?.length && testimonials[currentTestimonialIndex] && (
        <div key={currentTestimonialIndex} className="grid w-full grid-cols-4 gap-8 md:grid-cols-8 lg:grid-cols-12">
          <div className="col-span-4 flex flex-col items-start justify-start gap-8 md:gap-24 lg:col-span-5">
            <PortableText
              content={testimonials[currentTestimonialIndex].quote}
              variant={TextVariant.TextBlockquoteLG}
            />
            <Anchor
              aria-label={testimonials[currentTestimonialIndex].company?.text?.value ?? ''}
              className="mt-4 flex items-center justify-start gap-5 md:mt-0"
              href={testimonials[currentTestimonialIndex].company?.url || undefined}
            >
              {logoId && logoVisuals?.[logoId]}
              <div>
                <Text content={testimonials[currentTestimonialIndex].name?.value} variant={TextVariant.SubtitleMD} />
                <Text
                  content={testimonials[currentTestimonialIndex].company?.text?.value}
                  variant={TextVariant.SubtitleMD}
                />
              </div>
            </Anchor>
            {testimonials && testimonials.length > 1 && (
              <div className="flex w-full gap-3">
                <Button size="icon" variant="outline" onClick={handleMoveBackwards} aria-label="Previous">
                  <ArrowLeftIcon width={24} height={24} className="text-foreground" />
                </Button>
                <Button size="icon" variant="outline" onClick={handleMoveForward} aria-label="Next">
                  <ArrowRightIcon width={24} height={24} className="text-foreground" />
                </Button>
              </div>
            )}
          </div>

          {imageId && imageVisuals?.[imageId]}
        </div>
      )}
    </div>
  );
}
